<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import Card from '@uiKit/components/Card.vue'
import KpiInfoTooltip from './KpiInfoTooltip.vue'
import type { KpiInfoContent } from '../types/kpiTypes'

interface Props {
  value: string
  label: string
  iconBg: string
  iconColor: string
  icon: PiIconName
  badge?: { label: string; trend: 'up' | 'down' }
  info?: KpiInfoContent
}

defineProps<Props>()
</script>

<template>
  <Card variant="outlined" padding="0" direction="col" gap="3" class="kpi-card">
    <!-- Top row: icon box + optional badge -->
    <Box align="start" justify="between" class="kpi-card__top">
      <Box
        flex
        align="center"
        justify="center"
        class="kpi-card__icon-box"
        :style="{ background: iconBg }"
      >
        <PiIcon :name="icon" class="kpi-card__icon" :style="{ color: iconColor }" />
      </Box>
      <Box
        v-if="badge"
        align="center"
        gap="1"
        class="kpi-card__badge"
        :class="`kpi-card__badge--${badge.trend}`"
      >
        <PiIcon
          name="arrow-up"
          class="kpi-card__badge-icon"
          :class="`kpi-card__badge-icon--${badge.trend}`"
        />
        <Text type="caption-sm" is="span" class="kpi-card__badge-label">{{ badge.label }}</Text>
      </Box>
    </Box>

    <!-- Value -->
    <Text type="page-title" is="span" class="kpi-card__value">{{ value }}</Text>

    <!-- Label + info -->
    <Box align="end" justify="between" class="kpi-card__footer">
      <Text
        type="caption-sm"
        color="muted"
        transform="uppercase"
        is="span"
        class="kpi-card__label"
      >
        {{ label }}
      </Text>
      <KpiInfoTooltip
        v-if="info"
        :label="info.label"
        :title="info.title"
        :description="info.description"
        :formula="info.formula"
      >
        <Box is="button" flex align="center" justify="center" class="kpi-card__info" type="button">
          <PiIcon name="info-circle" class="kpi-card__info-icon" />
        </Box>
      </KpiInfoTooltip>
    </Box>
  </Card>
</template>

<style scoped>
.kpi-card {
  flex: 1;
  padding: 14px 1rem;
  /* Neutral border — primary-100 was pulling cards toward purple and
     fighting with the KPI icon color stories. */
  border-color: var(--p-content-border-color);
  min-width: 0;
}

.kpi-card__icon-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
}

.kpi-card__icon {
  font-size: 16px;
}

.kpi-card__badge {
  display: inline-flex;
  height: 22px;
  padding: 2px 6px;
  border-radius: 100px;
  border: 1px solid var(--p-success-300);
  background: var(--p-success-50);
  flex-shrink: 0;
}

.kpi-card__badge--down {
  border-color: var(--p-danger-300);
  background: var(--p-danger-50);
}

.kpi-card__badge-icon {
  font-size: 10px;
}

.kpi-card__badge-icon--up {
  color: var(--p-success-700);
}

.kpi-card__badge-icon--down {
  color: var(--p-danger-700);
  transform: rotate(180deg);
}

.kpi-card__badge-label {
  line-height: 1;
  color: var(--p-success-700);
  white-space: nowrap;
}

.kpi-card__badge--down .kpi-card__badge-label {
  color: var(--p-danger-700);
}

@media (max-width: 992px) {
  .kpi-card__top {
    flex-wrap: wrap;
    gap: 6px;
  }

  .kpi-card__badge {
    order: -1;
    flex-basis: 100%;
  }
}

.kpi-card__value {
  line-height: 1;
}

.kpi-card__label {
  line-height: 1.1;
  letter-spacing: 0.04em;
}

.kpi-card__info {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.kpi-card__info-icon {
  font-size: 14px;
  color: var(--p-surface-400);
  transition: color 0.15s;
}

.kpi-card__info:hover .kpi-card__info-icon {
  color: var(--p-text-color);
}
</style>
